import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import './ImageGalleryPage.scss';
import DarkNavbar from '../landingPage/header/DarkNavbar';
import Footer from '../landingPage/footer';
import { Image, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Gallery } from 'react-grid-gallery';
import ReactPlayer from 'react-player';
import { PORTAL_BASE_URL } from '../../env';

const AI_CREATOR_URL = '/ai-creator';
const CDN_BASE_URL = 'https://d48f7equ64qjl.cloudfront.net/';
const API_URL = 'https://gqlip0wnzg.execute-api.us-west-2.amazonaws.com/production/ai-asset-generator-helper';

const formatDimensions = (w, h, d) => {
  const parts = [];
  if (h != null) parts.push(`${h}H`);
  if (w != null) parts.push(`${w}W`);
  if (d != null) parts.push(`${d}D`);
  return parts.join(' x ') || '';
};

const toFullUrl = (path) => {
  if (!path) return '';
  return path.startsWith('http') ? path : CDN_BASE_URL + path;
};

const toCompressedUrl = (url) => {
  if (!url) return '';
  return url.replace(/\.png$/i, '_compressed.webp');
};

const toThumbnailUrl = (url) => {
  if (!url) return '';
  return url.replace(/\.(png|jpg|jpeg)$/i, '_512.png');
};

const transformApiData = (data) => {
  return data.map((item) => {
    const thumbnailUrl = toFullUrl(item.thumbnail);
    const fullUrl = item.uri ? toFullUrl(item.uri) : thumbnailUrl;
    const isVideo = item.image_type === 'ai_video';
    const siloUrl = toFullUrl(item.processed_silo);

    return {
      id: item.id,
      src: isVideo ? toThumbnailUrl(thumbnailUrl) : toThumbnailUrl(fullUrl),
      srcFallback: isVideo ? thumbnailUrl : (toCompressedUrl(fullUrl) || fullUrl),
      url: isVideo ? thumbnailUrl : fullUrl,
      videoUrl: isVideo ? fullUrl : null,
      hoverUrl: toCompressedUrl(siloUrl),
      hoverUrlFallback: siloUrl,
      width: item.width || 1,
      height: item.height || 1,
      dimensions: formatDimensions(item.product_width, item.product_height, item.product_depth),
      image_type: item.image_type,
      display_name: item.display_name,
    };
  });
};

const PlayIcon = () => (
  <div className="image-gallery__play-icon">
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="23" fill="rgba(0,0,0,0.55)" stroke="white" strokeWidth="2" />
      <polygon points="19,14 37,24 19,34" fill="white" />
    </svg>
  </div>
);

const GalleryThumbnail = ({ item, imageProps }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isVideo = item.image_type === 'ai_video';

  const handleImgError = (e) => {
    if (item.srcFallback && e.target.src !== item.srcFallback) {
      e.target.src = item.srcFallback;
    }
  };

  const handleHoverImgError = (e) => {
    if (item.hoverUrlFallback && e.target.src !== item.hoverUrlFallback) {
      e.target.src = item.hoverUrlFallback;
    }
  };

  return (
    <div
      className="image-gallery__item"
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img
        {...imageProps}
        alt={item.display_name || 'AI generated product'}
        className={`image-gallery__item-img ${isHovered && item.hoverUrl && !isVideo ? 'image-gallery__item-img--hidden' : ''}`}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
        onError={handleImgError}
      />

      {isVideo && <PlayIcon />}

      {item.hoverUrl && !isVideo && (
        <div className={`image-gallery__item-hover ${isHovered ? 'image-gallery__item-hover--visible' : ''}`}>
          <div className="image-gallery__item-hover-body">
            <img
              src={item.hoverUrl}
              alt="Product silo"
              className="image-gallery__item-hover-img"
              loading="lazy"
              onError={handleHoverImgError}
            />
          </div>
          <div className="image-gallery__item-hover-footer">
            <span className="image-gallery__item-hover-name">Product Image</span>
            <span className="image-gallery__item-hover-dims">{item.dimensions}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const FullscreenViewer = ({ src, alt, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="gallery-fullscreen" onClick={onClose}>
      <button className="gallery-fullscreen__close" onClick={onClose} type="button">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <img src={src} alt={alt} className="gallery-fullscreen__img" onClick={(e) => e.stopPropagation()} />
    </div>
  );
};

const VideoModal = ({ videoUrl, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="video-modal" onClick={onClose}>
      <div className="video-modal__content" onClick={(e) => e.stopPropagation()}>
        <button className="video-modal__close" onClick={onClose} type="button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <ReactPlayer
          url={videoUrl}
          playing
          controls
          width="100%"
          height="100%"
        />
      </div>
    </div>
  );
};

const ComparisonModal = ({ artwork, onClose, onPrev, onNext }) => {
  const [fullscreenSrc, setFullscreenSrc] = useState(null);
  const [fullscreenAlt, setFullscreenAlt] = useState('');

  const handleKeyDown = useCallback((e) => {
    if (fullscreenSrc) return;
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') onPrev();
    if (e.key === 'ArrowRight') onNext();
  }, [onClose, onPrev, onNext, fullscreenSrc]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const openFullscreen = (src, alt) => {
    setFullscreenSrc(src);
    setFullscreenAlt(alt);
  };

  return (
    <div className="gallery-modal" onClick={onClose}>
      <div className="gallery-modal__content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="gallery-modal__header">
          <span className="gallery-modal__badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l1.912 5.813a2 2 0 001.272 1.272L21 12l-5.813 1.912a2 2 0 00-1.272 1.272L12 21l-1.912-5.813a2 2 0 00-1.272-1.272L3 12l5.813-1.912a2 2 0 001.272-1.272L12 3z" />
            </svg>
            AI
          </span>
          <h2 className="gallery-modal__title">Product to AI-Generated Lifestyle Image</h2>
          <button className="gallery-modal__close" onClick={onClose} type="button">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Images */}
        <div className="gallery-modal__body">
          <div className="gallery-modal__images">
            {/* Product silo */}
            <div className="gallery-modal__image-col">
              <div
                className="gallery-modal__image-wrapper gallery-modal__image-wrapper--silo"
                onClick={() => openFullscreen(artwork.hoverUrl || artwork.url, 'Product silo')}
              >
                <img src={artwork.hoverUrl || artwork.url} alt="Product silo" />
                {artwork.dimensions && (
                  <span className="gallery-modal__dims-badge">{artwork.dimensions}</span>
                )}
              </div>
              <span className="gallery-modal__label">Product Photo</span>
            </div>

            {/* AI lifestyle */}
            <div className="gallery-modal__image-col">
              <div
                className="gallery-modal__image-wrapper gallery-modal__image-wrapper--lifestyle"
                onClick={() => openFullscreen(artwork.url, 'AI-Generated lifestyle')}
              >
                <img src={artwork.url} alt="AI-Generated lifestyle" />
              </div>
              <span className="gallery-modal__label">AI-Generated Image</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation arrows */}
      <button className="gallery-modal__nav gallery-modal__nav--prev" onClick={(e) => { e.stopPropagation(); onPrev(); }} type="button">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>
      <button className="gallery-modal__nav gallery-modal__nav--next" onClick={(e) => { e.stopPropagation(); onNext(); }} type="button">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>

      {fullscreenSrc && (
        <FullscreenViewer
          src={fullscreenSrc}
          alt={fullscreenAlt}
          onClose={() => setFullscreenSrc(null)}
        />
      )}
    </div>
  );
};

const PAGE_SIZE = 30;

const ImageGalleryPage = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState(null);
  const [toast, setToast] = useState({ visible: false, text: '', type: 'loading' });
  const previewContainerRef = useRef(null);
  const previewObserverRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const observerRef = useRef(null);
  const hasMoreRef = useRef(true);

  const handleCtaClick = () => {
    const utm_source = localStorage.getItem('utm_source') || null;
    const redirectPath = `/select-subscription?plan=free_trial&plan_type=smb_3d_services&utm_source=${utm_source}`;
    window.location.href = `${PORTAL_BASE_URL}/register?redirectURL=${encodeURIComponent(redirectPath)}`;
  };

  const fetchImages = useCallback((offset) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (offset > 0) setLoadingMore(true);

    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'get_ai_image_public_library', limit: PAGE_SIZE, offset }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.result === 'success' && Array.isArray(json.data)) {
          const newImages = transformApiData(json.data);
          setGalleryImages((prev) => [...prev, ...newImages]);
          offsetRef.current = offset + json.data.length;
          if (json.data.length < PAGE_SIZE) {
            setHasMore(false);
            hasMoreRef.current = false;
          }
        }
      })
      .catch((err) => {
        console.error('Failed to fetch gallery images:', err);
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
        loadingRef.current = false;
      });
  }, []);

  useEffect(() => {
    fetchImages(0);
  }, [fetchImages]);

  const sentinelRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current && !loadingRef.current) {
          fetchImages(offsetRef.current);
        }
      },
      { rootMargin: '200px' }
    );
    observerRef.current.observe(node);
  }, [fetchImages]);

  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const handleCardClick = (artwork) => {
    if (artwork.videoUrl) {
      setActiveVideoUrl(artwork.videoUrl);
      return;
    }
    setPreviewSrc(toCompressedUrl(artwork.url));
    setPreviewName(artwork.display_name || '');
    setIsPreviewOpen(true);
  };

  const saveAsBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const showToast = (text, type = 'loading', autoHide = true) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast({ visible: true, text, type });
    if (autoHide) {
      toastTimeoutRef.current = setTimeout(() => {
        setToast({ visible: false, text: '', type: 'loading' });
      }, 2000);
    }
  };

  const downloadFile = (url, filename, extension = null) => {
    showToast('Preparing to download..', 'loading', false);

    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.setRequestHeader('Access-Control-Allow-Origin', '*');
    xhr.setRequestHeader('Cache-Control', 'no-cache');
    xhr.responseType = 'blob';
    xhr.onload = function onLoad() {
      if (this.status === 200) {
        const blob = this.response;
        const name = extension ? `${filename}.${extension}` : filename;
        saveAsBlob(blob, name);
        showToast('Download started', 'success');
      } else {
        showToast('Download failed', 'error');
      }
    };
    xhr.onerror = function onError() {
      showToast('Download failed', 'error');
    };
    xhr.send();
  };

  const handleDownload = () => {
    if (!previewSrc) return;
    const extensionMatch = previewSrc.match(/\.(png|jpe?g|webp)(\?|#|$)/i);
    const extension = extensionMatch ? extensionMatch[1] : null;
    const filename = previewName || 'downloaded-image';
    downloadFile(previewSrc, filename, extension);
  };

  const ensureDownloadButton = () => {
    if (!previewContainerRef.current) return;
    const toolbar = previewContainerRef.current.querySelector('.ant-image-preview-operations');
    if (!toolbar) return;

    let downloadBtn = toolbar.querySelector('.image-gallery__download');
    if (!downloadBtn) {
      downloadBtn = document.createElement('span');
      downloadBtn.className = 'ant-image-preview-operations-operation image-gallery__download';
      downloadBtn.setAttribute('role', 'button');
      downloadBtn.setAttribute('aria-label', 'Download image');
      downloadBtn.setAttribute('tabindex', '0');
      downloadBtn.onclick = handleDownload;
      downloadBtn.onkeydown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleDownload();
        }
      };

      const downloadIcon = document.createElement('span');
      downloadIcon.role = 'img';
      downloadIcon.ariaLabel = 'download';
      downloadIcon.className = 'anticon anticon-download ant-image-preview-operations-icon';

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '64 64 896 896');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('data-icon', 'download');
      svg.setAttribute('width', '1em');
      svg.setAttribute('height', '1em');
      svg.setAttribute('fill', 'currentColor');
      svg.setAttribute('aria-hidden', 'true');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M505.7 661a8 8 0 0012.6 0l112-141.7a8 8 0 00-9.4-12.5h-71.3v-192c0-4.4-3.6-8-8-8h-64c-4.4 0-8 3.6-8 8v192h-71.3a8 8 0 00-9.4 12.5l112 141.7zM294 824h436c4.4 0 8-3.6 8-8v-64c0-4.4-3.6-8-8-8H294c-4.4 0-8 3.6-8 8v64c0 4.4 3.6 8 8 8z');

      svg.appendChild(path);
      downloadIcon.appendChild(svg);
      downloadBtn.appendChild(downloadIcon);
      toolbar.appendChild(downloadBtn);
    }

    downloadBtn.setAttribute('data-src', previewSrc || '');
  };

  const ensurePreviewLoader = () => {
    if (!previewContainerRef.current) return;
    const preview = previewContainerRef.current.querySelector('.ant-image-preview-img');
    const content = previewContainerRef.current.querySelector('.ant-image-preview-content');
    if (!content) return;

    let loader = content.querySelector('.image-gallery__preview-loader');
    if (!loader) {
      loader = document.createElement('div');
      loader.className = 'image-gallery__preview-loader';
      content.appendChild(loader);
      ReactDOM.render(<LoadingOutlined spin style={{ fontSize: 48, color: '#fff' }} />, loader);
    }

    if (preview) {
      if (preview.complete) {
        loader.style.display = 'none';
      } else {
        loader.style.display = 'flex';
        preview.onload = () => {
          loader.style.display = 'none';
        };
        preview.onerror = () => {
          loader.style.display = 'none';
        };
      }
    }
  };

  useEffect(() => {
    if (!isPreviewOpen) return undefined;

    ensureDownloadButton();
    ensurePreviewLoader();

    if (previewObserverRef.current) {
      previewObserverRef.current.disconnect();
    }

    previewObserverRef.current = new MutationObserver(() => {
      ensureDownloadButton();
      ensurePreviewLoader();
    });

    if (previewContainerRef.current) {
      previewObserverRef.current.observe(previewContainerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (previewObserverRef.current) {
        previewObserverRef.current.disconnect();
        previewObserverRef.current = null;
      }
    };
  }, [isPreviewOpen, previewSrc]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
        toastTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <DarkNavbar />
      <div className="image-gallery">
        <div className="image-gallery__hero">
          <div className="image-gallery__hero-text">
            <h1 className="image-gallery__title manrope">
              AI Images and Videos for Ecommerce
            </h1>
          </div>
          <button
            className="image-gallery__cta-btn"
            onClick={handleCtaClick}
          >
            Try AI images for free
          </button>
        </div>

        <div className="image-gallery__grid">
          {loading ? (
            <div className="image-gallery__loading">
              <span className="image-gallery__loading-spinner" />
              <span>Loading gallery...</span>
            </div>
          ) : galleryImages.length > 0 ? (
            <>
              <Gallery
                images={galleryImages}
                onClick={(index) => handleCardClick(galleryImages[index])}
                rowHeight={300}
                margin={8}
                enableImageSelection={false}
                thumbnailImageComponent={GalleryThumbnail}
              />
              {hasMore && (
                <div ref={sentinelRef} className="image-gallery__loading-more">
                  {loadingMore && (
                    <>
                      <span className="image-gallery__loading-spinner" />
                      <span>Loading more...</span>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="image-gallery__empty">No images available</div>
          )}
        </div>

        <Image
          key={previewSrc}
          src={previewSrc}
          style={{ display: 'none' }}
          preview={{
            visible: isPreviewOpen,
            onVisibleChange: (visible) => {
              setIsPreviewOpen(visible);
            },
            getContainer: () => {
              if (!previewContainerRef.current) {
                previewContainerRef.current = document.createElement('div');
                document.body.appendChild(previewContainerRef.current);
              }
              return previewContainerRef.current;
            },
          }}
        />

        <div className="image-gallery__footer-cta">
          <a href={AI_CREATOR_URL} className="image-gallery__learn-btn">
            Learn more about AI images
          </a>
        </div>
      </div>
      <Footer />

      {!isPreviewOpen && !activeVideoUrl && (
        <div className="image-gallery__floating-cta">
          <button
            className="image-gallery__cta-btn image-gallery__cta-btn--floating"
            onClick={handleCtaClick}
          >
            Try AI images for free
          </button>
        </div>
      )}

      {toast.visible && (
        <div className={`image-gallery__toast image-gallery__toast--${toast.type}`}>
          {toast.type === 'loading' && <span className="image-gallery__toast-spinner" />}
          <span>{toast.text}</span>
        </div>
      )}

      {activeVideoUrl && (
        <VideoModal
          videoUrl={activeVideoUrl}
          onClose={() => setActiveVideoUrl(null)}
        />
      )}
    </>
  );
};

export default ImageGalleryPage;
